import { map as _map, map } from 'lodash';
import { IDropdownItem } from '@shared/interfaces/dropdown/dropdown-item.interface';
import { INestedDropdownChildItem } from '@shared/interfaces/nested-dropdown-filter/nested-dropdown-filter-child.interface';
import { INestedDropdownItem } from '@shared/interfaces/nested-dropdown-filter/nested-dropdown-filter.interfaces';

type DropdownValue = { toString(): string };

type NestedDropdownSourceItem<IdKey extends PropertyKey, TextKey extends PropertyKey> = Record<
  IdKey | TextKey,
  DropdownValue
>;

type NestedDropdownItemFromSource = Omit<INestedDropdownItem, 'children'> & {
  children: INestedDropdownChildItem[] | null;
};

export class DropdownUtil {
  // In the keys array, the third item is the optional fallback key to look for text if the first key is not present
  static convertToDropdownItems<IdKey extends PropertyKey, T extends Record<IdKey, DropdownValue>>(
    items: T[],
    keys: [IdKey, keyof T, (keyof T)?]
  ): IDropdownItem[] {
    const [idKey, textKey, fallbackTextKey] = keys;

    return map(items, (item: T) => ({
      id: item[idKey].toString(),
      text: (
        item[textKey] ?? (fallbackTextKey === undefined ? undefined : item[fallbackTextKey])
      )?.toString(),
    }));
  }

  static convertObjectKeyValuePairsToDropdownItems<
    A extends number | string,
    B extends DropdownValue,
  >(object: Record<A, B>): IDropdownItem[] {
    return Object.entries<B>(object).map(([key, value]) => ({
      id: key.toString(),
      text: value.toString(),
    }));
  }

  static convertArrayToDropdownItems(arr: string[]): IDropdownItem[] {
    return arr.map(item => ({ id: item, text: item }));
  }

  static getNestedDropdownItems<
    IdKey extends PropertyKey,
    TextKey extends PropertyKey,
    ChildrenKey extends PropertyKey,
    T extends NestedDropdownSourceItem<IdKey, TextKey> &
      Record<ChildrenKey, NestedDropdownSourceItem<IdKey, TextKey>[] | null>,
  >(
    items: T[],
    keys: [IdKey, TextKey, ChildrenKey],
    isSelected: boolean
  ): NestedDropdownItemFromSource[] {
    const [idKey, textKey, childrenKey] = keys;

    return [
      ..._map(items, (item: T) => {
        const children = item[childrenKey];

        return {
          isSelected,
          id: item[idKey].toString(),
          text: item[textKey].toString(),
          children:
            children !== null
              ? DropdownUtil.getNestedChildItems(children, [idKey, textKey], isSelected)
              : null,
        };
      }),
    ];
  }

  static getNestedChildItems<
    IdKey extends PropertyKey,
    TextKey extends PropertyKey,
    T extends NestedDropdownSourceItem<IdKey, TextKey>,
  >(items: T[], keys: [IdKey, TextKey], isSelected: boolean): INestedDropdownChildItem[] {
    const [idKey, textKey] = keys;

    return [
      ..._map(items, (item: T) => ({
        isSelected,
        id: item[idKey].toString(),
        text: item[textKey].toString(),
      })),
    ];
  }

  static getDropdownItemFromText(text: string, items: IDropdownItem[]): IDropdownItem | null {
    const item = items.find(h => h.text === text);
    return item || null;
  }

  static getDropdownItemFromId(
    id: string | number | null | undefined,
    items: IDropdownItem[]
  ): IDropdownItem | null {
    if (id === null || id === undefined) {
      return null;
    }

    const lookupId = String(id);
    const item = items.find(h => h.id === lookupId);
    return item || null;
  }
}
